// src/RegistrationPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchJson, API_BASE } from './api';

const COUNTRY_PREFIXES = [
  { code: 'IT', label: 'IT +39', value: '+39' },
  { code: 'FR', label: 'FR +33', value: '+33' },
  { code: 'ES', label: 'ES +34', value: '+34' },
  { code: 'DE', label: 'DE +49', value: '+49' },
  { code: 'GB', label: 'UK +44', value: '+44' },
];

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@300;400;500;600;700;800;900&family=DM+Serif+Display:ital@0;1&family=Space+Mono:wght@400;700&display=swap');
  .jkl-reg-root *, .jkl-reg-root *::before, .jkl-reg-root *::after { box-sizing: border-box; }
  .jkl-reg-root {
    --black:#07070a;--deep:#0a0a0f;--card:#0f0f16;--border:rgba(255,255,255,0.07);
    --gold:#d4a550;--gold-lt:#f0c878;--gold-dim:rgba(212,165,80,0.10);
    --white:#f0ece4;--muted:#8a8a9a;--faint:#2a2a38;--error:#f87171;
    min-height:100vh;background:var(--black);color:var(--white);
    font-family:'Kanit',sans-serif;display:grid;grid-template-columns:1fr 1fr;position:relative;
  }
  .jkl-reg-root::before{content:'';position:fixed;inset:0;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");pointer-events:none;z-index:0;opacity:0.4;}
  .jkl-reg-left{background:var(--deep);border-right:1px solid var(--border);display:flex;flex-direction:column;padding:60px;position:relative;overflow:hidden;}
  .jkl-reg-left::before{content:'';position:absolute;bottom:-100px;left:-100px;width:500px;height:500px;background:radial-gradient(ellipse,rgba(212,165,80,0.06) 0%,transparent 65%);pointer-events:none;}
  .jkl-reg-left::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(212,165,80,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(212,165,80,0.03) 1px,transparent 1px);background-size:48px 48px;pointer-events:none;}
  .jkl-reg-left-logo{font-family:'Kanit',sans-serif;font-size:18px;font-weight:900;letter-spacing:0.2em;color:var(--white);margin-bottom:auto;position:relative;z-index:1;}
  .jkl-reg-left-main{position:relative;z-index:1;flex:1;display:flex;flex-direction:column;justify-content:center;}
  .jkl-reg-eyebrow{font-family:'Space Mono',monospace;font-size:9px;letter-spacing:0.2em;color:var(--gold);text-transform:uppercase;margin-bottom:16px;}
  .jkl-reg-headline{font-family:'Kanit',sans-serif;font-size:42px;font-weight:900;text-transform:uppercase;letter-spacing:-0.02em;line-height:0.95;margin-bottom:24px;}
  .jkl-reg-headline .dim{color:var(--faint);}
  .jkl-reg-body{font-family:'Kanit',sans-serif;font-size:14px;font-weight:300;color:var(--muted);line-height:1.7;max-width:360px;margin-bottom:40px;}
  .jkl-reg-body strong{color:var(--white);font-weight:500;}
  .jkl-benefits{list-style:none;display:flex;flex-direction:column;gap:12px;}
  .jkl-benefit{display:flex;align-items:flex-start;gap:12px;}
  .jkl-benefit-icon{width:20px;height:20px;border-radius:50%;background:var(--gold-dim);border:1px solid rgba(212,165,80,0.2);display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px;font-size:10px;color:var(--gold);}
  .jkl-benefit-text{font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.05em;color:var(--muted);line-height:1.5;}
  .jkl-benefit-text strong{color:var(--white);}
  .jkl-reg-left-footer{position:relative;z-index:1;font-family:'Space Mono',monospace;font-size:9px;letter-spacing:0.1em;color:rgba(212,165,80,0.2);}
  .jkl-reg-right{display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:60px 72px;background:var(--black);overflow-y:auto;position:relative;z-index:1;}
  .jkl-reg-wrap{width:100%;max-width:400px;animation:jklRegFadeUp 0.6s ease forwards;padding:20px 0;}
  @keyframes jklRegFadeUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
  .jkl-reg-form-logo{font-family:'Kanit',sans-serif;font-size:20px;font-weight:900;letter-spacing:0.2em;text-transform:uppercase;color:var(--white);margin-bottom:8px;}
  .jkl-reg-form-eyebrow{font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.15em;color:var(--gold);text-transform:uppercase;margin-bottom:32px;}
  .jkl-type-selector{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:28px;}
  .jkl-type-btn{padding:14px 12px;background:var(--card);border:1px solid var(--border);border-radius:8px;color:var(--muted);font-family:'Kanit',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;text-align:center;line-height:1.3;}
  .jkl-type-btn .icon{font-size:18px;display:block;margin-bottom:4px;}
  .jkl-type-btn:hover{border-color:rgba(212,165,80,0.3);color:var(--white);}
  .jkl-type-btn.active{border-color:var(--gold);background:var(--gold-dim);color:var(--white);}
  .jkl-reg-field{margin-bottom:16px;}
  .jkl-reg-field label{display:block;font-family:'Space Mono',monospace;font-size:9px;letter-spacing:0.18em;color:var(--muted);text-transform:uppercase;margin-bottom:8px;}
  .jkl-reg-field input,.jkl-reg-field select{width:100%;padding:13px 16px;background:var(--card);border:1px solid var(--border);border-radius:6px;color:var(--white);font-family:'Kanit',sans-serif;font-size:15px;font-weight:400;outline:none;transition:border-color 0.2s,background 0.2s;-webkit-appearance:none;}
  .jkl-reg-field input::placeholder{color:var(--faint);}
  .jkl-reg-field input:focus,.jkl-reg-field select:focus{border-color:rgba(212,165,80,0.5);background:#12121a;}
  .jkl-reg-field select option{background:#0f0f16;}
  .jkl-reg-grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
  .jkl-phone-grid{display:grid;grid-template-columns:130px 1fr;gap:8px;}
  .jkl-reg-pwd-wrap{position:relative;}
  .jkl-reg-pwd-wrap input{padding-right:44px;}
  .jkl-reg-toggle-pwd{position:absolute;right:14px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--muted);font-size:14px;padding:4px;transition:color 0.2s;}
  .jkl-reg-toggle-pwd:hover{color:var(--gold);}
  .jkl-autocomplete-wrap{position:relative;}
  .jkl-suggestions{position:absolute;top:100%;left:0;right:0;background:#13161c;border:1px solid rgba(212,165,80,0.2);border-radius:6px;margin-top:4px;z-index:100;overflow:hidden;box-shadow:0 8px 24px rgba(0,0,0,0.4);}
  .jkl-suggestion-item{padding:10px 14px;font-family:'Kanit',sans-serif;font-size:14px;color:var(--white);cursor:pointer;transition:background 0.15s;border-bottom:1px solid rgba(255,255,255,0.04);}
  .jkl-suggestion-item:last-child{border-bottom:none;}
  .jkl-suggestion-item:hover{background:rgba(212,165,80,0.08);color:var(--gold);}
  .jkl-reg-error{background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.25);border-radius:6px;padding:12px 16px;font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.05em;color:#f87171;margin-bottom:20px;}
  .jkl-reg-divider{height:1px;background:var(--border);margin:20px 0;}
  .jkl-reg-btn{width:100%;padding:15px;background:var(--gold);border:none;border-radius:6px;color:var(--black);font-family:'Kanit',sans-serif;font-size:14px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;margin-bottom:24px;display:flex;align-items:center;justify-content:center;gap:8px;}
  .jkl-reg-btn:hover:not(:disabled){background:var(--gold-lt);transform:translateY(-1px);box-shadow:0 6px 24px rgba(212,165,80,0.25);}
  .jkl-reg-btn:disabled{opacity:0.5;cursor:not-allowed;}
  .jkl-reg-spinner{width:16px;height:16px;border:2px solid rgba(0,0,0,0.3);border-top-color:#07070a;border-radius:50%;animation:jklRegSpin 0.7s linear infinite;}
  @keyframes jklRegSpin{to{transform:rotate(360deg);}}
  .jkl-reg-link-row{text-align:center;font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.08em;color:var(--muted);text-transform:uppercase;}
  .jkl-reg-link-row button{background:none;border:none;cursor:pointer;color:var(--gold);font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;transition:color 0.2s;padding:0;}
  .jkl-reg-link-row button:hover{color:var(--gold-lt);}
  @media(max-width:768px){
    .jkl-reg-root{grid-template-columns:1fr;}
    .jkl-reg-left{display:none;}
    .jkl-reg-right{padding:60px 32px;min-height:100vh;}
  }
`;

const RUOLI_FREELANCE = [
  'Assistente al Montaggio','Autore al montaggio','Colorist','Coordinatore di Post Produzione',
  'Data Manager','Editor Video / Montatore (Junior)','Editor Video / Montatore','Editor Video / Montatore (Senior)',
  'Finalizzatore','Ingest','Logger','Motion Graphic Designer','Sound Designer / Montatore del Suono','VFX Artist',
  'Assistente di Produzione','Delegato di Produzione','Direttore di Produzione','Ispettore di Produzione',
  'Location Manager','Organizzatore Generale','Segretaria di Edizione','Segretario di produzione',
  'Capo Redattore','Caposervizio','Direttore','Fotografo','Giornalista','Grafico','Redattore',
  'Social Media Manager','Video-giornalista',
  'Aiuto Regista','Assistente alla Regia','Assistente Operatore','Autore Set TV','Collaboratore ai testi',
  'Costumista','Direttore della Fotografia','Fonico di Presa Diretta','Gaffer','Machinista',
  'Make-up Artist','Operatore di Camera','Operatore di Ripresa','Organizzatore di Set','Parrucchiere',
  'Primo Assistente Camera (1AC)','Runner','Scenografo','Secondo Assistente Camera (2AC)',
  'Steadicam Operator','Tecnico Luci','Truccatrice','Video Assist',
].sort();

const COUNTRY_CODES = ['AE','AR','AT','AU','BE','BR','CA','CH','DE','DK','ES','FI','FR','GB','GR','IT','JP','MX','NL','NO','PL','PT','RO','SE','US','OTHER'];

const CITIES = ['Alessandria','Ancona','Aosta','Arezzo','Asti','Bari','Benevento','Bergamo','Bolzano','Bologna','Brescia','Brindisi','Cagliari','Campobasso','Caserta','Catania','Catanzaro','Como','Cosenza','Cuneo','Estero','Ferrara','Firenze','Foggia','Frosinone','Genova','L\'Aquila','La Spezia','Latina','Lecce','Livorno','Messina','Milano','Modena','Napoli','Novara','Nuoro','Padova','Palermo','Parma','Pescara','Perugia','Pisa','Potenza','Ravenna','Reggio Calabria','Reggio Emilia','Rieti','Rimini','Roma','Salerno','Sassari','Savona','Siena','Taranto','Teramo','Torino','Trento','Treviso','Trieste','Udine','Varese','Venezia','Verona','Vicenza','Viterbo'];

export default function RegistrationPage({ onRegisterSuccess, onGoLogin, onGoTermini }) {
  const { t } = useTranslation();
  const [userType, setUserType] = useState('freelance');
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [countryPrefix, setCountryPrefix] = useState('+39');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [ruolo, setRuolo] = useState('');
  const [country, setCountry] = useState('IT');
  const [companyRole, setCompanyRole] = useState('coordinator');
  const [cities, setCities] = useState([]);
  const [availableTravel, setAvailableTravel] = useState(false);
  const [availableRemote, setAvailableRemote] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!document.getElementById('jkl-reg-styles')) {
      const style = document.createElement('style');
      style.textContent = STYLES;
      style.id = 'jkl-reg-styles';
      document.head.appendChild(style);
    }
    return () => { document.getElementById('jkl-reg-styles')?.remove(); };
  }, []);

  const searchCompanies = useCallback(async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    try {
      const res = await fetch(`${API_BASE}/maestranze/companies/search?q=${encodeURIComponent(q)}`);
      const data = await res.json().catch(() => []);
      setSuggestions(Array.isArray(data) ? data : []);
      setShowSuggestions(true);
    } catch { setSuggestions([]); }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!nome.trim()) return setError(t('reg.err_nome'));
    if (!cognome.trim()) return setError(t('reg.err_cognome'));
    if (!email || !/\S+@\S+\.\S+/.test(email)) return setError(t('reg.err_email'));
    if (userType === 'freelance' && !phoneLocal) return setError(t('reg.err_phone'));
    if (userType === 'freelance' && !ruolo) return setError(t('reg.err_role'));
    if (!password || password.length < 6) return setError(t('reg.err_password'));
    if (!acceptedTerms) return setError(t('reg.err_terms'));
    setLoading(true);
    try {
      const phone = phoneLocal ? `${countryPrefix}${phoneLocal.replace(/\D/g, '')}` : null;
      const data = await fetchJson('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email, password,
          user_type: userType,
          nome: nome.trim(),
          cognome: cognome.trim(),
          telefono: phone,
          company_name: userType === 'production_house' ? (companyName || null) : null,
          ruolo: userType === 'freelance' ? ruolo : undefined,
          country: country || 'IT',
          company_role: userType === 'production_house' ? companyRole : undefined,
          cities: userType === 'freelance' ? cities : undefined,
          available_travel: userType === 'freelance' ? availableTravel : undefined,
          available_remote: userType === 'freelance' ? availableRemote : undefined,
        }),
      });
      onRegisterSuccess?.(data);
    } catch (err) {
      setError(err.message || t('reg.err_generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="jkl-reg-root">
      <div className="jkl-reg-left">
        <div className="jkl-reg-left-logo">JKL</div>
        <div className="jkl-reg-left-main">
          <p className="jkl-reg-eyebrow">{t('reg.left_eyebrow')}</p>
          <h2 className="jkl-reg-headline">
            {t('reg.left_h1')}<br/>
            <span className="dim">{t('reg.left_h2')}</span><br/>
            {t('reg.left_h3')}
          </h2>
          <p className="jkl-reg-body" dangerouslySetInnerHTML={{ __html: t('reg.left_body') }} />
          <ul className="jkl-benefits">
            {['b1','b2','b3','b4'].map(k => (
              <li key={k} className="jkl-benefit">
                <span className="jkl-benefit-icon">✓</span>
                <span className="jkl-benefit-text" dangerouslySetInnerHTML={{ __html: t(`reg.${k}`) }} />
              </li>
            ))}
          </ul>
        </div>
        <div className="jkl-reg-left-footer">{t('reg.left_footer')}</div>
      </div>

      <div className="jkl-reg-right">
        <div className="jkl-reg-wrap">
          <div className="jkl-reg-form-logo">JKL</div>
          <div className="jkl-reg-form-eyebrow">{t('reg.eyebrow')}</div>

          <div className="jkl-type-selector">
            <button type="button" className={`jkl-type-btn ${userType==='freelance'?'active':''}`} onClick={() => setUserType('freelance')}>
              <span className="icon">🎬</span>{t('reg.type_freelance')}
            </button>
            <button type="button" className={`jkl-type-btn ${userType==='production_house'?'active':''}`} onClick={() => setUserType('production_house')}>
              <span className="icon">🏭</span>{t('reg.type_production')}
            </button>
          </div>

          {error && <div className="jkl-reg-error">{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="jkl-reg-grid2">
              <div className="jkl-reg-field">
                <label>{t('reg.label_nome')}</label>
                <input value={nome} onChange={e => setNome(e.target.value)} placeholder={t('reg.ph_nome')} />
              </div>
              <div className="jkl-reg-field">
                <label>{t('reg.label_cognome')}</label>
                <input value={cognome} onChange={e => setCognome(e.target.value)} placeholder={t('reg.ph_cognome')} />
              </div>
            </div>

            <div className="jkl-reg-field">
              <label>{t('reg.label_email')}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('reg.ph_email')} autoComplete="email" />
            </div>

            {userType === 'freelance' && (
              <div className="jkl-reg-field">
                <label>{t('reg.label_phone')}</label>
                <div className="jkl-phone-grid">
                  <select value={countryPrefix} onChange={e => setCountryPrefix(e.target.value)}>
                    {COUNTRY_PREFIXES.map(p => <option key={p.code} value={p.value}>{p.label}</option>)}
                  </select>
                  <input type="tel" value={phoneLocal} onChange={e => setPhoneLocal(e.target.value.replace(/\D/g,''))} placeholder={t('reg.ph_phone')} inputMode="numeric" />
                </div>
              </div>
            )}

            {userType === 'freelance' && (
              <div className="jkl-reg-field">
                <label>{t('reg.label_role')}</label>
                <select value={ruolo} onChange={e => setRuolo(e.target.value)} required>
                  <option value="">{t('reg.ph_role')}</option>
                  {RUOLI_FREELANCE.map(r => <option key={r} value={r}>{t('roles.' + r)}</option>)}
                </select>
              </div>
            )}

            {userType === 'freelance' && (
              <div className="jkl-reg-field">
                <label>{t('reg.label_zones')}</label>
                <select value="" onChange={e => { const c = e.target.value; if (c && !cities.includes(c)) setCities(prev => [...prev, c]); e.target.value = ''; }}
                  style={{width:'100%',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#f0ece4',padding:'10px 12px',fontSize:13,outline:'none'}}>
                  <option value="">{t('reg.ph_city')}</option>
                  {CITIES.filter(c => !cities.includes(c)).map(c => <option key={c} value={c}>{c === 'Estero' ? t('reg.city_abroad') : c}</option>)}
                </select>
                {cities.length > 0 && (
                  <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:10}}>
                    {cities.map(c => (
                      <span key={c} style={{background:'rgba(212,165,80,0.12)',border:'1px solid rgba(212,165,80,0.35)',borderRadius:20,padding:'4px 12px',fontSize:12,color:'#d4a550',display:'flex',alignItems:'center',gap:6}}>
                        {c}
                        <button type="button" onClick={() => setCities(prev => prev.filter(x => x !== c))} style={{background:'none',border:'none',color:'#d4a550',cursor:'pointer',fontSize:14,lineHeight:1,padding:0}}>×</button>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:14}}>
                  {[['availableTravel',availableTravel,setAvailableTravel,'reg.travel'],['availableRemote',availableRemote,setAvailableRemote,'reg.remote']].map(([key,val,setter,tkey]) => (
                    <label key={key} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}} onClick={() => setter(v => !v)}>
                      <div style={{width:36,height:20,borderRadius:10,background:val?'#d4a550':'rgba(255,255,255,0.1)',transition:'background 0.2s',position:'relative',flexShrink:0}}>
                        <div style={{width:16,height:16,borderRadius:'50%',background:'#fff',position:'absolute',top:2,left:val?18:2,transition:'left 0.2s'}} />
                      </div>
                      <span style={{fontSize:13,color:val?'#f0ece4':'#8a8a9a'}}>{t(tkey)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="jkl-reg-field">
              <label>{t('reg.label_country')}</label>
              <select value={country} onChange={e => setCountry(e.target.value)}
                style={{width:'100%',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#f0ece4',padding:'10px 12px',fontSize:13,outline:'none'}}>
                {COUNTRY_CODES.map(code => <option key={code} value={code}>{t(`countries.${code}`)}</option>)}
              </select>
            </div>

            {userType === 'production_house' && (
              <div className="jkl-reg-field">
                <label>{t('reg.label_company_role')}</label>
                <div style={{display:'flex',gap:12,marginTop:4}}>
                  {[['coordinator','reg.coord_title','reg.coord_sub'],['supervisor','reg.sup_title','reg.sup_sub']].map(([role,titleKey,subKey]) => (
                    <div key={role} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',flex:1,background:companyRole===role?'rgba(212,165,80,0.12)':'rgba(255,255,255,0.03)',border:companyRole===role?'2px solid rgba(212,165,80,0.6)':'1px solid rgba(255,255,255,0.08)',borderRadius:10,padding:'12px 14px',transition:'all 0.15s'}} onClick={() => setCompanyRole(role)}>
                      <div style={{width:18,height:18,borderRadius:'50%',border:companyRole===role?'2px solid #d4a550':'2px solid rgba(255,255,255,0.2)',background:companyRole===role?'#d4a550':'transparent',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s'}}>
                        {companyRole===role && <div style={{width:8,height:8,borderRadius:'50%',background:'#1a1d24'}} />}
                      </div>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:companyRole===role?'#d4a550':'#f0ece4'}}>{t(titleKey)}</div>
                        <div style={{fontSize:11,color:'#8a8a9a'}}>{t(subKey)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {userType === 'production_house' && (
              <div className="jkl-reg-field">
                <label>{t('reg.label_company')}</label>
                <div className="jkl-autocomplete-wrap">
                  <input value={companyName} onChange={e => { setCompanyName(e.target.value); searchCompanies(e.target.value); }} onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} placeholder={t('reg.ph_company')} autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="jkl-suggestions">
                      {suggestions.map(s => (
                        <div key={s.id} className="jkl-suggestion-item" onMouseDown={() => { setCompanyName(s.name); setShowSuggestions(false); }}>{s.name}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="jkl-reg-divider" />

            <div className="jkl-reg-field">
              <label>{t('reg.label_password')}</label>
              <div className="jkl-reg-pwd-wrap">
                <input type={showPwd?'text':'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder={t('reg.ph_password')} autoComplete="new-password" />
                <button type="button" className="jkl-reg-toggle-pwd" onClick={() => setShowPwd(v => !v)}>{showPwd?'🙈':'👁'}</button>
              </div>
            </div>

            <div style={{marginBottom:'20px',display:'flex',alignItems:'flex-start',gap:'10px'}}>
              <input type="checkbox" id="acceptTerms" checked={acceptedTerms} onChange={e => { setAcceptedTerms(e.target.checked); setError(''); }} style={{marginTop:'3px',accentColor:'#d4a550',cursor:'pointer',width:'15px',height:'15px',flexShrink:0}} />
              <label htmlFor="acceptTerms" style={{fontFamily:'Space Mono, monospace',fontSize:'10px',letterSpacing:'0.05em',color:'#8a8a9a',lineHeight:'1.6',cursor:'pointer'}}>
                {t('reg.terms_pre')}{' '}
                <button type="button" onClick={onGoTermini} style={{background:'none',border:'none',cursor:'pointer',color:'#d4a550',fontFamily:'Space Mono, monospace',fontSize:'10px',letterSpacing:'0.05em',padding:0,textDecoration:'underline'}}>
                  {t('reg.terms_link')}
                </button>
                {` ${t('reg.terms_post')}`}
              </label>
            </div>

            <button type="submit" className="jkl-reg-btn" disabled={loading}>
              {loading ? <span className="jkl-reg-spinner" /> : t('reg.submit')}
            </button>
          </form>

          <div className="jkl-reg-link-row">
            {t('reg.has_account')}&nbsp;
            <button onClick={onGoLogin}>{t('reg.login_link')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
